import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import type { AdminSession, Branding } from '@almox/shared';
import { api } from './api/client.js';

/**
 * Porta de entrada do Almox. O endereco e publico: sem isto, qualquer um que
 * descobrisse a URL veria e mexeria no estoque de casa. E o mesmo login simples
 * da area administrativa (um usuario), como a fase 1 permite.
 */

const SEM_SESSAO: AdminSession = { authenticated: false, username: null, usingDefaultPassword: false };
const SEM_LOGO: Branding = { hasLogo: false, updatedAt: null };

interface AuthValue {
  session: AdminSession;
  branding: Branding;
  /** URL do logo com cache-bust, ou null quando nenhum foi enviado. */
  logoUrl: string | null;
  refreshSession: () => Promise<void>;
  refreshBranding: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue>({
  session: SEM_SESSAO,
  branding: SEM_LOGO,
  logoUrl: null,
  refreshSession: async () => undefined,
  refreshBranding: async () => undefined,
  logout: async () => undefined,
});

export const useAuth = (): AuthValue => useContext(AuthContext);

function logoUrlDe(branding: Branding): string | null {
  if (!branding.hasLogo) return null;
  // O timestamp evita o navegador servir o logo antigo depois de trocar.
  return `/api/v1/branding/logo?v=${encodeURIComponent(branding.updatedAt ?? '')}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [branding, setBranding] = useState<Branding>(SEM_LOGO);

  const refreshSession = useCallback(async () => {
    try {
      setSession(await api.adminSession());
    } catch {
      setSession(SEM_SESSAO);
    }
  }, []);

  const refreshBranding = useCallback(async () => {
    try {
      setBranding(await api.branding());
    } catch {
      setBranding(SEM_LOGO);
    }
  }, []);

  useEffect(() => {
    void refreshSession();
    void refreshBranding();
  }, [refreshSession, refreshBranding]);

  // Sessao expirada no meio do uso: o cliente avisa e a tela volta ao login.
  useEffect(() => {
    const aoPerderSessao = () => setSession(SEM_SESSAO);
    window.addEventListener('almox:unauthorized', aoPerderSessao);
    return () => window.removeEventListener('almox:unauthorized', aoPerderSessao);
  }, []);

  // O logo do sistema tambem vira o icone da aba.
  useEffect(() => {
    const url = logoUrlDe(branding);
    if (!url) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = url;
  }, [branding]);

  const logout = useCallback(async () => {
    await api.adminLogout().catch(() => undefined);
    setSession(SEM_SESSAO);
  }, []);

  const valor: AuthValue = {
    session: session ?? SEM_SESSAO,
    branding,
    logoUrl: logoUrlDe(branding),
    refreshSession,
    refreshBranding,
    logout,
  };

  if (session === null) {
    return (
      <div className="gate">
        <p className="muted">Carregando…</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={valor}>
      {session.authenticated ? children : <LoginScreen onEntered={setSession} />}
    </AuthContext.Provider>
  );
}

function LoginScreen({ onEntered }: { onEntered: (session: AdminSession) => void }) {
  const { logoUrl } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErro(null);
    try {
      onEntered(await api.adminLogin(username, password));
    } catch (falha) {
      setPassword('');
      setErro((falha as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gate">
      <form className="gate-card" onSubmit={submit}>
        {logoUrl ? (
          <img src={logoUrl} alt="Almox" className="gate-logo" />
        ) : (
          <h1 className="gate-title">Almox</h1>
        )}
        <p className="small muted gate-sub">O estoque da sua casa.</p>

        <div className="field">
          <label htmlFor="login-user">Usuario</label>
          <input
            id="login-user"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label htmlFor="login-pass">Senha</label>
          <input
            id="login-pass"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {erro ? <p className="gate-erro">{erro}</p> : null}

        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </div>
  );
}
